import first from "lodash/first";
import { useRouter } from "next/router";
import { useMemo } from "react";
import { graphql, usePaginationFragment } from "react-relay";

import { useInfiniteScrollTriggerReference } from "scmp-app/lib/hooks";
import type { hooksPostiesVideoVideo$key } from "scmp-app/queries/__generated__/hooksPostiesVideoVideo.graphql";
import type { videoPostiesPageQuery } from "scmp-app/queries/__generated__/videoPostiesPageQuery.graphql";

export const usePostiesVideoList = (queue: hooksPostiesVideoVideo$key, pageSize: number) => {
  const {
    data,
    hasNext: hasNextPage,
    isLoadingNext: isLoading,
    loadNext,
  } = usePaginationFragment<videoPostiesPageQuery, hooksPostiesVideoVideo$key>(
    graphql`
      fragment hooksPostiesVideoVideo on Query
      @argumentDefinitions(after: { type: "String" }, count: { type: "Int", defaultValue: 9 })
      @refetchable(queryName: "postiesVideoListPaginationQuery") {
        queue(filter: { name: "video_section_picks_515863" }) {
          items(after: $after, first: $count) @connection(key: "hooksPostiesVideo__items") {
            edges {
              node {
                ... on Video {
                  entityId
                  ...landingVideoVideo
                  ...postiesVideoCardVideo
                }
              }
            }
          }
        }
      }
    `,
    queue,
  );

  const { infiniteScrollTriggerReference } = useInfiniteScrollTriggerReference({
    hasNextPage,
    isLoading,
    onLoadMore: () => {
      loadNext(pageSize);
    },
  });

  const videos = useMemo(
    () => (data?.queue?.items?.edges ?? []).map(({ node }) => node),
    [data?.queue?.items?.edges],
  );

  const router = useRouter();
  const currentEntityId = Array.isArray(router.query.entityId)
    ? first(router.query.entityId)
    : (router.query.entityId ?? null);
  const filteredCurrentVideos = videos.filter(video => video.entityId !== currentEntityId);

  return {
    hasNextPage,
    infiniteScrollTriggerReference,
    videos: filteredCurrentVideos,
  };
};
